import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { RangePicker } from '@/components';
import { DateFormats } from '@/constants/dates';
import { DatePresetKey } from '@/models/cancelledOrders.model';
import { StyledCustomPreset, StyledPresetBar, StyledPresetButton, StyledPresetLabel } from './DatePresetBarStyles';

export interface IDatePresetBarProps {
  preset: DatePresetKey;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  onPresetChange: (preset: DatePresetKey) => void;
  onCustomRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
  presets?: DatePresetKey[];
}

const DEFAULT_PRESETS: DatePresetKey[] = [
  'all_time',
  'current_year',
  'last_month',
  'current_month',
  'last_7_days',
  'last_30_days',
];

const DatePresetBar = ({
  preset,
  dateFrom,
  dateTo,
  onPresetChange,
  onCustomRangeChange,
  presets = DEFAULT_PRESETS,
}: IDatePresetBarProps) => {
  const { t } = useTranslation(['dashboard']);

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const from = dateStrings[0] ? dayjs(dateStrings[0], DateFormats.EUROPEAN_DEFAULT) : null;
    const to = dateStrings[1] ? dayjs(dateStrings[1], DateFormats.EUROPEAN_DEFAULT) : null;
    onCustomRangeChange(from, to);
  };

  return (
    <StyledPresetBar>
      {presets.map((key) => (
        <StyledPresetButton key={key} type="button" $active={preset === key} onClick={() => onPresetChange(key)}>
          <StyledPresetLabel>{t(`cancelled_orders_preset_${key}`)}</StyledPresetLabel>
        </StyledPresetButton>
      ))}
      <StyledCustomPreset $active={preset === 'custom'}>
        <StyledPresetLabel>{t('cancelled_orders_preset_custom')}</StyledPresetLabel>
        <RangePicker
          value={(dateFrom && dateTo ? [dateFrom, dateTo] : null) as never}
          format={DateFormats.EUROPEAN_DEFAULT}
          onChange={handleDateRangeChange}
          placeholder={[t('cancelled_orders_date_from'), t('cancelled_orders_date_to')]}
          disabledDate={(current) => Boolean(current && current.valueOf() > Date.now())}
        />
      </StyledCustomPreset>
    </StyledPresetBar>
  );
};

export default DatePresetBar;
